/**
 * StatusPill — colored-dot + label combo used for verification + SSL +
 * link-status states across Domains, AddDomain, Pages.
 *
 * Four tones map to DESIGN.md container colors so they feel like
 * first-class brand surfaces, not a tacked-on traffic-light strip:
 *
 *   success — green container (#7dffa2 family)   ← live / verified / active
 *   pending — tertiary (peach) container          ← awaiting / in-progress
 *   error   — error container (red)               ← failed / blocked
 *   neutral — white-glass                         ← unknown / disabled
 */

type Tone = 'success' | 'pending' | 'error' | 'neutral';

const TONE: Record<Tone, { bg: string; dot: string; text: string }> = {
  success: {
    bg: 'rgba(125, 255, 162, 0.12)',
    dot: '#7dffa2',
    text: '#7dffa2',
  },
  pending: {
    bg: 'rgba(255, 182, 136, 0.12)',
    dot: '#ffb688',
    text: '#ffb688',
  },
  error: {
    bg: 'rgba(255, 180, 171, 0.12)',
    dot: '#ffb4ab',
    text: '#ffb4ab',
  },
  neutral: {
    bg: 'rgba(255, 255, 255, 0.06)',
    dot: '#cac3d8',
    text: '#cac3d8',
  },
};

export function StatusPill({ tone, label }: { tone: Tone; label: string }) {
  const t = TONE[tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em]"
      style={{ background: t.bg, color: t.text }}
    >
      <span
        aria-hidden
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: t.dot, boxShadow: `0 0 8px ${t.dot}` }}
      />
      {label}
    </span>
  );
}
